// Pitch evaluation score calculation utilities

import type { Evaluation, IndividualMetricEvaluation } from '../types/pitch.types'

export const PERSONA_LABELS: Record<string, string> = {
  oracle: 'The Oracle',
  hustler: 'The Hustler',
  architect: 'The Architect',
  hawk: 'The Hawk',
}

export type JudgeScore = {
  persona: string
  label: string
  score: number
}

export type MetricScore = {
  metric: string
  label: string
  score: number
}

export const METRIC_LABELS: Record<string, string> = {
  market: 'Market',
  problem_solution: 'Problem Solution',
  team: 'Team',
  traction: 'Traction',
  vision: 'Vision',
}

/**
 * Get metric scores for display on the pitch card.
 * If a winner persona exists, shows that persona's per-metric scores.
 * Otherwise, computes average scores per metric across all personas.
 */
export function getMetricScores(
  evaluations: IndividualMetricEvaluation[],
  winnerPersona: string | null
): MetricScore[] {
  if (!evaluations.length || !winnerPersona) return []

  const winnerEvals = evaluations.filter((e) => e.persona === winnerPersona && e.score !== null)
  return winnerEvals.map((e) => ({
    metric: e.metric,
    label: METRIC_LABELS[e.metric] ?? e.metric,
    score: e.score as number,
  }))
}

export type WinnerJudge = {
  persona: string
  label: string
}

export function extractEvaluationData(evaluation: Evaluation | null | undefined) {
  const individualEvaluations = evaluation?.individualMetricEvaluations ?? []
  const collectiveMetricEvaluations = evaluation?.collectiveMetricEvaluations ?? []
  const winnerPersona = evaluation?.winnerPersona ?? null
  const metricScores = getMetricScores(individualEvaluations, winnerPersona)
  const totalScore = evaluation?.score ?? undefined

  // For custom persona evaluations, show persona name instead of archetype label
  const winnerJudge: WinnerJudge | null = evaluation?.personaName
    ? { persona: evaluation.personaName, label: evaluation.personaName }
    : winnerPersona
      ? { persona: winnerPersona, label: PERSONA_LABELS[winnerPersona] ?? winnerPersona }
      : null

  return {
    individualEvaluations,
    collectiveMetricEvaluations,
    metricScores,
    totalScore,
    winnerJudge,
  }
}
